import { useEffect, useState, useCallback } from 'react';
import { useInjectedService } from './useInjectedService';

interface WebSocketHook {
  isConnected: boolean;
  on: <T = unknown>(event: string, listener: (data: T) => void) => void;
  off: <T = unknown>(event: string, listener?: (data: T) => void) => void;
  emit: (event: string, ...args: unknown[]) => void;
}

/**
 * useWebSocket Hook - Platform Agnostic
 * 
 * Custom hook for managing real-time connection lifecycle and events.
 * Consumes the injected IRealtimeService for multi-platform readiness.
 * 
 * @param _url - The WebSocket URL (now managed by the service & registry)
 * @param isAuthenticated - Whether user is authenticated (controls connection)
 */
const useWebSocket = (_url: string, isAuthenticated: boolean = false): WebSocketHook => {
  const realtimeService = useInjectedService('realtime');
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initializeConnection = async () => {
      if (!isAuthenticated) {
        realtimeService.disconnect();
        if (isMounted) setIsConnected(false);
        return;
      }

      // Dynamically load token to prevent circular dependencies at the top level
      const { getToken } = await import('../utils/apiClient');
      const token = getToken();

      if (!token) {
        realtimeService.disconnect();
        if (isMounted) setIsConnected(false);
        return;
      }

      // Establish universal connection
      realtimeService.connect(token);
    };

    // Initialize connectivity
    initializeConnection();

    // Subscribe to platform-agnostic connection status updates
    const unsubscribe = realtimeService.subscribeToStatus((connected) => {
      if (isMounted) setIsConnected(connected);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [isAuthenticated, realtimeService]);

  const on = useCallback(<T = unknown>(event: string, listener: (data: T) => void) => {
    realtimeService.on<T>(event, listener);
  }, [realtimeService]);

  const off = useCallback(<T = unknown>(event: string, listener?: (data: T) => void) => {
    realtimeService.off<T>(event, listener);
  }, [realtimeService]);

  const emit = useCallback((event: string, ...args: unknown[]) => {
    realtimeService.emit(event, ...args);
  }, [realtimeService]);

  return {
    isConnected,
    on,
    off,
    emit,
  };
};

export default useWebSocket;
